<template>
  <div class="max-pos-calendar" data-max-host>
    <div v-if="isSchedule" class="max-pos-calendar__nav">
      <button type="button" class="btn btn-default btn-sm" :disabled="iface.busy.value" v-on:click="shift(-1)">{{ _('Previous') }}</button>
      <span class="max-pos-calendar__range">{{ rangeLabel }}</span>
      <button type="button" class="btn btn-default btn-sm" :disabled="iface.busy.value" v-on:click="shift(1)">{{ _('Next') }}</button>
    </div>
    <div v-if="!groups.length && !isSchedule" class="text-muted">{{ node.empty || _('Nothing to show.') }}</div>
    <template v-if="isSchedule">
      <div v-if="!days.length" class="text-muted">{{ node.empty || _('Nothing to show.') }}</div>
      <div v-else class="table-responsive">
        <table class="table table-bordered max-pos-calendar__grid">
          <thead>
            <tr>
              <th v-if="hourly" class="max-pos-calendar__hour"></th>
              <th v-for="day in days" :key="day.key">{{ day.label }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!hourly">
              <td v-for="day in days" :key="day.key">
                <div
                  v-for="row in eventsOn(day)"
                  :key="row.key"
                  class="max-pos-calendar__event"
                  v-on:click="open(row)"
                >
                  <span class="max-pos-calendar__time">{{ timeLabel(row.row) }}</span>
                  <span class="max-pos-calendar__name">{{ eventName(row.row) }}</span>
                </div>
              </td>
            </tr>
            <tr v-for="hour in hours" v-else :key="hour">
              <td class="max-pos-calendar__hour">{{ String(hour).padStart(2, '0') }}:00</td>
              <td v-for="day in days" :key="day.key + '-' + hour">
                <div
                  v-for="row in eventsOn(day, hour)"
                  :key="row.key"
                  class="max-pos-calendar__event"
                  v-on:click="open(row)"
                >
                  <span class="max-pos-calendar__name">{{ eventName(row.row) }}</span>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
    <div v-else>
      <div v-for="group in groups" :key="group.key" class="max-pos-calendar__day">
        <h4>{{ group.label }}</h4>
        <div
          v-for="row in group.rows"
          :key="row.key"
          class="max-pos-calendar__event"
          v-on:click="open(row)"
        >
          <span class="max-pos-calendar__time">{{ timeLabel(row.row) }}</span>
          <span class="max-pos-calendar__name">{{ eventName(row.row) }}</span>
          <span v-if="row.row.client_name" class="text-muted">{{ row.row.client_name }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { useMaxInterface } from '#max-runtime/context.ts'

export default {
  props: ['node'],
  setup () {
    const { $translate } = useNuxtApp()
    return { iface: useMaxInterface(), _: $translate }
  },
  computed: {
    timezone () {
      return (this.$store && this.$store.getters.timezone) || 'Europe/Helsinki'
    },
    isSchedule () {
      return this.node.kind === 'showtimes' || this.node.kind === 'product_schedule' || this.node.kind === 'resource_schedule'
    },
    hourly () {
      return this.node.mode === 'hourly'
    },
    fromDate () {
      return this.parseIso(this.node.fromValue)
    },
    toDate () {
      return this.parseIso(this.node.toValue) || this.fromDate
    },
    days () {
      const start = this.fromDate
      const end = this.toDate
      if (!start || !end) return []
      const out = []
      let cursor = start.startOf('day')
      const last = end.startOf('day')
      while (cursor <= last && out.length < 31) {
        out.push({
          key: cursor.toISODate(),
          label: cursor.toFormat('ccc d.L.'),
          date: cursor
        })
        cursor = cursor.plus({ days: 1 })
      }
      return out
    },
    hours () {
      return this.hourly ? Array.from({ length: 24 }, (_v, i) => i) : []
    },
    rangeLabel () {
      if (!this.days.length) return ''
      const first = this.days[0].date
      const last = this.days[this.days.length - 1].date
      return first.toFormat('d.L.yyyy') + (first.toISODate() === last.toISODate() ? '' : '–' + last.toFormat('d.L.yyyy'))
    },
    groups () {
      const buckets = { starting: this._('Starting'), ending: this._('Ending'), ongoing: this._('Ongoing') }
      const by = new Map()
      for (const row of this.node.resolvedRows || []) {
        const event = row.row || {}
        const key = this.node.kind === 'agenda' && event.bucket
          ? String(event.bucket)
          : String(event.start_ts ? DateTime.fromSeconds(Number(event.start_ts), { zone: this.timezone }).toISODate() : 'other')
        if (!by.has(key)) {
          by.set(key, {
            key,
            label: buckets[key] || (event.start_ts
              ? DateTime.fromSeconds(Number(event.start_ts), { zone: this.timezone }).toFormat('cccc d.L.')
              : this._('Reservations')),
            rows: []
          })
        }
        by.get(key).rows.push(row)
      }
      return [...by.values()]
    }
  },
  methods: {
    parseIso (value) {
      if (!value) return null
      const dt = DateTime.fromISO(String(value), { zone: this.timezone })
      return dt.isValid ? dt : null
    },
    eventsOn (day, hour) {
      return (this.node.resolvedRows || []).filter(row => {
        const event = row.row || {}
        if (!event.start_ts) return false
        const start = DateTime.fromSeconds(Number(event.start_ts), { zone: this.timezone })
        if (start.toISODate() !== day.key) return false
        return hour === undefined || start.hour === hour
      })
    },
    eventName (event) {
      return event.product_name || event.name || event.section || ''
    },
    timeLabel (event) {
      if (!event || !event.start_ts) return ''
      const start = DateTime.fromSeconds(Number(event.start_ts), { zone: this.timezone })
      const end = event.end_ts ? DateTime.fromSeconds(Number(event.end_ts), { zone: this.timezone }) : null
      return end ? start.toFormat('HH:mm') + '–' + end.toFormat('HH:mm') : start.toFormat('HH:mm')
    },
    shift (direction) {
      const start = this.fromDate
      const end = this.toDate || start
      if (!start) return
      const days = Math.max(1, Math.round(end.diff(start, 'days').days) + 1)
      const nextFrom = start.plus({ days: days * direction })
      const nextTo = end.plus({ days: days * direction })
      const from = nextFrom.toISODate()
      const to = nextTo.toISODate()
      const change = this.node.on && this.node.on.change
      if (change) this.iface.run(change, { ...(this.node.scope || {}), event: { from, to } })
    },
    open (row) {
      const open = this.node.open
      if (!open) return
      this.iface.run([{ do: 'navigate', to: open.to, id: open.id || { var: 'row.id' } }], { ...(this.node.scope || {}), row: row.row })
    }
  }
}
</script>

<style scoped>
.max-pos-calendar__nav {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.max-pos-calendar__range {
  font-weight: 600;
}
.max-pos-calendar__day {
  margin-bottom: 16px;
}
.max-pos-calendar__event {
  display: flex;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}
.max-pos-calendar__grid .max-pos-calendar__event {
  flex-direction: column;
  gap: 2px;
  padding: 4px 0;
}
.max-pos-calendar__time {
  min-width: 90px;
  font-variant-numeric: tabular-nums;
}
.max-pos-calendar__hour {
  width: 64px;
  font-variant-numeric: tabular-nums;
}
.max-pos-calendar__name {
  font-weight: 600;
}
</style>
